import { Button, ForumCodeDocIcon, ForumDocIcon, WhiteboardIcon } from "@minervaproject/fds";
import { useContext, useState } from "react";
import { useDrag } from "react-dnd";

import { createResourceHelper } from "api/api_helpers";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { collect, useDragHandler } from "hooks/use_drag_handler";
import { featureContentInOneUp } from "shared/reducers/classroom";
import { MinervaResourceSubtypeType, PaneContentType, ResourceTypeType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";

function NewMinervaResources() {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const [isLoading, setIsLoading] = useState(false);
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const breakout = useAppSelector(classroomSelectors.selectActiveBreakout());
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());
  const titlePrefix =
    breakout && breakoutRoom ? `Group ${breakoutRoom.title} (${breakout.title})` : "";
  const dragCallback =
    (
      type: ResourceTypeType,
      options: {
        minervaResourceSubtype?: MinervaResourceSubtypeType;
      },
    ) =>
    async () => {
      try {
        setIsLoading(true);
        const resource = await createResourceHelper(classId, type, dispatch, forumAPI, {
          minervaResourceSubtype: options.minervaResourceSubtype,
          titlePrefix,
        });
        setIsLoading(false);
        return resource?.id;
      } catch (e) {
        setIsLoading(false);
        window.flightLogger.error("[NewMinervaResources] dragCallback failed");
        throw e;
      }
    };
  const [collectedForumDoc, dragForumDoc] = useDrag({
    type: PaneContentType.Resource,
    item: {
      callback: dragCallback(ResourceTypeType.ForumDoc, {
        minervaResourceSubtype: MinervaResourceSubtypeType.ForumDoc,
      }),
    },
    collect,
  });
  const [collectedForumCodeDoc, dragForumCodeDoc] = useDrag({
    type: PaneContentType.Resource,
    item: {
      callback: dragCallback(ResourceTypeType.ForumDoc, {
        minervaResourceSubtype: MinervaResourceSubtypeType.ForumCodeDoc,
      }),
    },
    collect,
  });
  const [collectedWhiteboard, dragWhiteboard] = useDrag({
    type: PaneContentType.Resource,
    item: {
      callback: dragCallback(ResourceTypeType.Whiteboard, {}),
    },
    collect,
  });

  const isDragging =
    collectedForumDoc.isDragging ||
    collectedForumCodeDoc.isDragging ||
    collectedWhiteboard.isDragging;
  useDragHandler(isDragging);

  const handleNewMinervaResource = (subtype: MinervaResourceSubtypeType) => async () => {
    try {
      setIsLoading(true);
      const resource = await createResourceHelper(
        classId,
        ResourceTypeType.ForumDoc,
        dispatch,
        forumAPI,
        {
          minervaResourceSubtype: subtype,
          titlePrefix,
        },
      );
      setIsLoading(false);
      if (resource) {
        dispatch(
          featureContentInOneUp({
            contentId: resource.id,
            contentType: PaneContentType.Resource,
            breakoutRoomId: breakoutRoom?.id,
            ownerId: currentUserId,
          }),
        );
      }
    } catch (e) {
      setIsLoading(false);
      window.flightLogger.error("[Resources] handleNewMinervaResource failed");
      throw e;
    }
  };

  const handleNewWhiteboardResource = async () => {
    try {
      setIsLoading(true);
      const resource = await createResourceHelper(
        classId,
        ResourceTypeType.Whiteboard,
        dispatch,
        forumAPI,
        {
          titlePrefix,
        },
      );
      setIsLoading(false);
      if (resource) {
        dispatch(
          featureContentInOneUp({
            contentId: resource.id,
            contentType: PaneContentType.Resource,
            breakoutRoomId: breakoutRoom?.id,
            ownerId: currentUserId,
          }),
        );
      }
    } catch (e) {
      setIsLoading(false);
      window.flightLogger.error("[Resources] handleNewWhiteboardResource failed");
      throw e;
    }
  };

  return (
    <>
      <Button
        className="mb1 flex-none"
        kind="classroom-secondary"
        onClick={handleNewMinervaResource(MinervaResourceSubtypeType.ForumDoc)}
        disabled={isLoading}
        ref={dragForumDoc}
      >
        <ForumDocIcon />
        Forum Doc
      </Button>
      <Button
        className="mb1 flex-none"
        kind="classroom-secondary"
        onClick={handleNewMinervaResource(MinervaResourceSubtypeType.ForumCodeDoc)}
        disabled={isLoading}
        ref={dragForumCodeDoc}
      >
        <ForumCodeDocIcon />
        Forum Code Doc
      </Button>
      <Button
        className="mb1 flex-none"
        kind="classroom-secondary"
        onClick={handleNewWhiteboardResource}
        disabled={isLoading}
        ref={dragWhiteboard}
      >
        <WhiteboardIcon />
        Whiteboard
      </Button>
    </>
  );
}

export { NewMinervaResources };
